import React from 'react';
import {
    Button
} from 'shared/components/ui/button';
import {
    Input
} from 'shared/components/ui/input';
import {
    Plus, Trash2
} from 'lucide-react';
import {
    cn
} from 'shared/lib/utils';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from 'shared/components/ui/table';
import {
    OelEditor
} from '../../primitives/oel';
import FieldHelp from './FieldHelp';

/**
 * Simple-mode outputs: name + OEL expression.
 */
export default function SimpleTypedOutputsEditor({
    outputs, onChange, embedded = false, oelContext, oelPlaceholder
}) {
    const list = Array.isArray(outputs) ? outputs : [];

    function update(idx, patch) {
        const next = list.map((it, i) => (i === idx ? { ...it, ...patch } : it));
        onChange(next);
    }

    function add() {
        let i = list.length + 1;
        let name = `output_${i}`;
        while (list.some((x) => x.name === name)) name = `output_${++i}`;
        onChange([...list, {
            name,
            type: 'string',
            description: '',
            oel_expression: ''
        }]);
    }

    function remove(idx) {
        onChange(list.filter((_, i) => i !== idx));
    }

    const inputClass = embedded ? 'h-7 text-xs rounded-sm' : 'h-8 rounded-sm';
    const headClass = cn(
        'h-8 px-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground',
        embedded && 'h-7 text-[10px]'
    );
    const cellClass = 'px-2 py-1.5';

    return (
        <div className={cn('space-y-1.5', embedded && 'text-xs')}>
            {!embedded && (
                <FieldHelp>What values should this procedure return when it finishes?</FieldHelp>
            )}
            {list.length === 0 && embedded && (
                <p className="text-xs text-muted-foreground leading-snug">
                    None yet — add an output and set its OEL expression.
                </p>
            )}
            {list.length > 0 && (
                <div className="overflow-hidden rounded-sm border border-foreground/20">
                    <Table className="table-fixed w-full">
                        <colgroup>
                            <col className="w-1/3" />
                            <col className="w-2/3" />
                            <col className="w-9" />
                        </colgroup>
                        <TableHeader>
                            <TableRow className="border-foreground/20 hover:bg-transparent">
                                <TableHead className={cn(headClass, 'w-1/3')}>Name</TableHead>
                                <TableHead className={cn(headClass, 'w-2/3')}>OEL expression</TableHead>
                                <TableHead className={cn(headClass, 'w-9 text-right')} />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {list.map((out, idx) => (
                                <TableRow key={idx} className="border-foreground/15 hover:bg-muted/30">
                                    <TableCell className={cellClass}>
                                        <Input
                                            value={out.name || ''}
                                            onChange={(e) => update(idx, { name: e.target.value })}
                                            placeholder="e.g. result"
                                            className={inputClass}
                                        />
                                    </TableCell>
                                    <TableCell className={cellClass}>
                                        <OelEditor
                                            value={out.oel_expression || ''}
                                            onChange={(v) => update(idx, { oel_expression: v })}
                                            ctx={oelContext}
                                            placeholder={oelPlaceholder || '@step_1.output.results'}
                                            ariaLabel={`Output ${out.name || idx + 1} expression`}
                                            inline
                                            compact={embedded}
                                        />
                                    </TableCell>
                                    <TableCell className={cn(cellClass, 'text-right')}>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className={cn(
                                                'text-destructive',
                                                embedded ? 'h-7 w-7' : 'h-8 w-8'
                                            )}
                                            onClick={() => remove(idx)}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
            <Button
                variant="secondary"
                size="sm"
                className={cn(
                    'border border-primary/35 bg-primary/15 font-semibold text-primary shadow-none hover:bg-primary/25',
                    embedded && 'h-7 text-xs'
                )}
                onClick={add}
            >
                <Plus className="mr-1 h-3 w-3" /> Add
            </Button>
        </div>
    );
}
